// ContactTable.tsx
import React, { useEffect, useState } from "react";
import ReusableTable from "../../../../global components/Table/Table";
import useSWR from "swr";
import {
  parseClientError,
  swrFetcher,
  swrMutationFetcher,
  SwrMutationFetcherArgType,
} from "../../../../libs/api/Endpoints";
import { UseAuthToken } from "../../../../libs/api/userSession";
import { Button, Skeleton, Tooltip } from "antd";
import NoData from "../../../../global components/Table/NoData/NoData";
import dayjs from "dayjs";
import Paginate from "../../../../global components/Paginate/Pagination";
import SearchInput from "../../../../global components/Forms/SearchInput/SearchInput";
import s from "./Transactions.module.scss";
import { PaginatedTransactionData } from "../../../../libs/Utils/Types/Transactions"; // Make sure this type is defined
import SelectField from "../../../../global components/Forms/SelectField/SelectField";
import clsx from "clsx";
import TransactionsIcon from "../../../../global components/Icons/Transactions/Transactions";
import Unauthorised from "../../../../global components/Unauthorised";
import Card from "../../../../global components/Card/Card";
import { generateResult } from "../../../../global components/ResultModal/ResultModal";
import useSWRMutation from "swr/mutation";
type TransactionStat = {
  total: number;
  totalAmount: 25000.5;
  successful: {
    count: number;
    amount: number;
  };
  failed: {
    count: number;
    amount: number;
  };
  pending: {
    count: number;
    amount: number;
  };
};
function Transactions() {
  const token = UseAuthToken();
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageItems, setPageItems] = useState(50);
  const [filter, setFilter] = useState<string>("");
  const [type, setType] = useState<string>("");

  const [unauthorised, setUnauthorised] = useState<
    boolean | { title: string; message: string }
  >(false);
  const { data: transactions, isLoading } = useSWR<
    PaginatedTransactionData,
    any,
    any
  >(
    token
      ? [
          "api/admin/transactions",
          token,

          {
            page: currentPage,
            limit: pageItems,
            ...(searchValue && { search: searchValue }),
            ...(filter && { status: filter }),
            ...(type && { type }),
          },
        ]
      : null,
    swrFetcher,
    {
      revalidateOnFocus: true,

      shouldRetryOnError: false,
    },
  );
  const { trigger: Download, isMutating: loadings } = useSWRMutation(
    `api/admin/transactions/export${type && `?type=${type}`}`,
    swrMutationFetcher,
  );

  const {
    data: transactionStat,
    isLoading: loading,
    error: statError,
  } = useSWR<{ data: TransactionStat }, any, any>(
    token
      ? [
          "api/admin/transactions/stats",
          token,

          {
            page: currentPage,
            limit: pageItems,
            searchId: searchValue,
            ...(filter && { status: filter }),
          },
        ]
      : null,
    swrFetcher,
    {
      revalidateOnFocus: true,

      shouldRetryOnError: false,
    },
  );
  const handleSearch = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1); // Reset to the first page
  };

  const handlePageChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageItems(size);
  };
  function handleFilter(value: string) {
    setFilter(value);
  }
  function handleType(value: string) {
    setType(value);
  }
  // Define table columns
  const columns = [
    {
      title: "REFERENCE ID",
      dataIndex: "referenceId",
      key: "referenceId",
      render: (referenceId: string) => (
        <span className={s.credit}>{referenceId}</span>
      ),
    },

    {
      title: "RECIPIENT",
      dataIndex: "recipient",
      key: "recipient",
      render: (recipient: string) => (
        <span className={s.amount}>{recipient}</span>
      ),
    },
    {
      title: "TYPE",
      dataIndex: "type",
      key: "type",
      render: (type: string) => <span className={s.credit}>{type}</span>,
    },
    {
      title: "CREDIT",
      dataIndex: "amount",
      key: "amount",
      render: (amount: string) => <span className={s.amount}>{amount}</span>,
    },
    {
      title: "AMOUNT",
      dataIndex: "cost",
      key: "cost",
      render: (cost: string) => <span className={s.amount}>{cost}</span>,
    },
    {
      title: "NARRATION",
      dataIndex: "naration",
      key: "naration",
      render: (naration: string) => (
        <span className={s.naration} style={{cursor:"pointer"}}>
          <Tooltip title={naration} >
          {naration}</Tooltip></span>
      ),
    },
    {
      title: "DATE",
      dataIndex: "date",
      key: "date",
      render: (date: string) => (
        <span className={s.date}>
          {dayjs.utc(date).local().format("MMM D, YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <span className={clsx(s.status, s.statusCell, s[status.toLowerCase()])}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      ),
    },
  ];
  // Transform API response to table data
  const transformedData = transactions?.data.map((contact) => ({
    key: contact.id,
    referenceId: contact.slug || "-",
    // credit: (contact.credits && contact.invoice.toLocaleString()) || "N/A",
    recipient: contact.user.firstName + " " + contact.user.lastName || "N/A",
    type: contact.type,
    amount: contact.amount
      ? contact.amount.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "$" + 0.0,
    date: contact.createdAt || "-",
    cost: contact.costUsd
      ? `$${contact.costUsd?.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : "-",
    status: contact.status,
    naration:contact.narration||"-"
  }));

  // Handle error state
  useEffect(() => {
    if (statError) {
      const e = parseClientError(statError);
      if (e.statusCode === 403) {
        setUnauthorised(true);
      } else {
        setUnauthorised({
          title: "Error",
          message: e.message || "Something went wrong",
        });
      }
    }
  }, [statError]);

  // const rowSelection: TableProps<DataType>["rowSelection"] = {
  //   onChange: (selectedRowKeys: React.Key[], selectedRows: DataType[]) => {},
  // }
  const downloadCsv = async () => {
    try {
      const fetcherArg: SwrMutationFetcherArgType = {
        token,

        requestOption: {
          method: "GET",
        },
      };
      const result: Blob = await Download(fetcherArg);
      console.log(result);
      // Create a URL for the downloaded blob
      const url = window.URL.createObjectURL(result);

      // Create a temporary <a> element to trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "transactions.csv"); // Set the desired file name
      document.body.appendChild(link);
      link.click(); // Trigger the download
      document.body.removeChild(link); // Cleanup
    } catch (error) {
      const e = parseClientError(error);
      console.log(e.message);

      generateResult("error", e.message || "Something went wrong");
    }
  };

  return (
    <div className={s.wrapper}>
      <div className={s.header}>
        <div className={s.titles}>
          <h1 className={s.title}>Transactions</h1>
          <h3 className={s.subtitle}>Keep track of all credit purchases </h3>
        </div>
        <Button loading={loadings} onClick={downloadCsv} className={s.button}>
          Download CSV
        </Button>
      </div>
      <div className={s.cards}>
        {unauthorised ? (
          typeof unauthorised === "boolean" ? (
            <div className={s.cardError}>
              <Unauthorised />
            </div>
          ) : (
            <div className={s.cardError}>
              <Unauthorised error={unauthorised} />
            </div>
          )
        ) : (
          <>
            {loading ? (
              <Skeleton.Node
                active={loading}
                rootClassName={s.cardSkeleton}
                className={s.cardSkeleton}
              />
            ) : (
              <Card
                onClick={() => setFilter("")}
                label="Total transactions"
                stat={transactionStat?.data.total || 0}
              />
            )}
            {loading ? (
              <Skeleton.Node
                active={loading}
                rootClassName={s.cardSkeleton}
                className={s.cardSkeleton}
              />
            ) : (
              <Card
                onClick={() => setFilter("successful")}
                label="Successful"
                stat={transactionStat?.data.successful.count || 0}
              />
            )}
            {loading ? (
              <Skeleton.Node
                active={loading}
                rootClassName={s.cardSkeleton}
                className={s.cardSkeleton}
              />
            ) : (
              <Card
                onClick={() => setFilter("failed")}
                label="Failed"
                stat={transactionStat?.data.failed.count || 0}
              />
            )}
            {loading ? (
              <Skeleton.Node
                active={loading}
                rootClassName={s.cardSkeleton}
                className={s.cardSkeleton}
              />
            ) : (
              <Card
                onClick={() => {
                  setFilter("pending");
                }}
                label="Pending"
                stat={transactionStat?.data.pending.count || 0}
              />
            )}
          </>
        )}
      </div>
      <div className={s.table}>
        <div className={s.searchParams}>
          <div className={s.params}>
            <div className={s.searchInput}>
              <SearchInput
                placeholder="search ID"
                handleSearch={handleSearch}
              />
            </div>
            <SelectField
              bordered
              label={"Status:"}
              wrapperClassname={s.SelectWrapper}
              defaultValue={""}
              value={filter}
              onChange={(value: any) => handleFilter(value)}
              style={{ width: "100%", height: "100%" }}
              options={[
                { value: "", label: "All" }, // Option to reset the filter
                { value: "successful", label: "Successful" },
                { value: "pending", label: "Pending" },
                { value: "failed", label: "Failed" },
              ]}
            />
            <SelectField
              bordered
              label={"Type:"}
              wrapperClassname={s.SelectWrapper}
              defaultValue={""}
              value={type}
              onChange={(value: any) => handleType(value)}
              style={{ width: "100%", height: "100%" }}
              styles={{
                root: {
                  width: "fit-content",
                },
              }}
              options={[
                { value: "", label: "All" }, // Option to reset the filter
                { value: "usage", label: "Usage" },
                { value: "refund", label: "Refund" },
                { value: "adjustment", label: "Adjustment" },
                { value: "migration", label: "Migration" },
                { value: "auto_topup", label: "Auto-topup" },
                { value: "bonus", label: "Bonus" },
                { value: "reversal", label: "Reversal" },
                { value: "purchase", label: "Purchase" },
              ]}
            />
          </div>
          <div className={s.paginate}>
            <Paginate
              onChange={handlePageChange}
              total={transactions?.pagination.totalItems || 1}
              pageSize={pageItems}
            />
          </div>
        </div>

        <ReusableTable
          loading={isLoading}
          noDataComponent={
            unauthorised ? (
              typeof unauthorised === "boolean" ? (
                <Unauthorised />
              ) : (
                <Unauthorised error={unauthorised} />
              )
            ) : (
              <NoData
                Message="No transactions found"
                subMessage=""
                icon={
                  <TransactionsIcon fill="#010516" height={80} width={80} />
                }
              />
            )
          }
          dataSource={transformedData || []}
          columns={columns}
        />
      </div>
    </div>
  );
}

export default Transactions;
